import _ from 'underscore';

// TODO: Move the constants to a separate package in Shared Components so that with-site-map can consume CampaignTypeMapping from it
// Currently a similar CampaignTypeMapping is exported from with-site-map, but the package has too many unrelated dependencies
export const CampaignTypeMapping = {
  None: 0,
  Default: 1 << 0,
  Shopping: 1 << 1,
  DynamicSearchAds: 1 << 2,
  Audience: 1 << 3,
  Smart: 1 << 4,
  Hotel: 1 << 5,
  PerformanceMax: 1 << 6,
  App: 1 << 7,
  LinkedIn: 1 << 8,
  All: 0xFFFF,
};

export const CampaignTypes = {
  Default: 'Default', // Default is search
  Shopping: 'Shopping',
  DynamicSearchAds: 'DynamicSearchAds',
  Audience: 'Audience',
  Smart: 'Smart',
  Hotel: 'Hotel',
  PerformanceMax: 'PerformanceMax',
  App: 'App',
  LinkedIn: 'LinkedIn',
};

export const CampaignSubTypes = {
  AudienceStatic: 'AudienceStatic',
  AudienceFeedBased: 'AudienceFeedBased',
  AudienceDynamicFeedBased: 'AudienceDynamicFeedBased',
  AudienceVideoAds: 'AudienceVideoAds',
  BrandAwarenessVideoAds: 'BrandAwarenessVideoAds',
  DisplayAds: 'DisplayAds',
  ShoppingCoOperative: 'ShoppingCoOperative',
  ShoppingSponsoredProductAd: 'ShoppingSponsoredProductAd',
  ShoppingSmartAds: 'ShoppingSmartAds',
};

export const CampaignSubTypeMapping = {
  [CampaignSubTypes.AudienceStatic]: 0,
  [CampaignSubTypes.AudienceFeedBased]: 1,
  [CampaignSubTypes.AudienceDynamicFeedBased]: 2,
  [CampaignSubTypes.AudienceVideoAds]: 3,
  [CampaignSubTypes.BrandAwarenessVideoAds]: 4,
  [CampaignSubTypes.DisplayAds]: 5,
  [CampaignSubTypes.ShoppingCoOperative]: 10,
  [CampaignSubTypes.ShoppingSponsoredProductAd]: 11,
  [CampaignSubTypes.ShoppingSmartAds]: 12,
};

export const CampaignSubTypeTextMapping = _.invert(CampaignSubTypeMapping);

export const isXandrCampaign = (campaignSubType: string | number) =>
  campaignSubType === CampaignSubTypes.DisplayAds ||
  campaignSubType === CampaignSubTypes.BrandAwarenessVideoAds ||
  campaignSubType === CampaignSubTypeMapping.DisplayAds ||
  campaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds;

export const isAudienceNativeAdsCampaign = (campaignSubType: string | number) =>
  campaignSubType === CampaignSubTypes.AudienceStatic ||
  campaignSubType === CampaignSubTypeMapping.AudienceStatic;

export const isXandrDisplayAdsCampaign = (campaignSubType: string | number) =>
  campaignSubType === CampaignSubTypes.DisplayAds ||
  campaignSubType === CampaignSubTypeMapping.DisplayAds;

export const isXandrVideoAdsCampaign = (campaignSubType: string | number) =>
  campaignSubType === CampaignSubTypes.BrandAwarenessVideoAds ||
  campaignSubType === CampaignSubTypeMapping.BrandAwarenessVideoAds;

export const isSearchShoppingCampaign = (campaignType: string | number, campaignSubType: string | number) => {
  const isShoppingType = campaignType === CampaignTypes.Shopping || campaignType === CampaignTypeMapping.Shopping;
  const isSearchShopping = isShoppingType && !campaignSubType;

  return isSearchShopping;
};

export const isSearchCampaign = (campaignType: string | number) =>
  campaignType === CampaignTypes.Default ||
  campaignType === CampaignTypeMapping.Default;

export const isShoppingCampaign = (campaignType: string | number) =>
  campaignType === CampaignTypes.Shopping ||
  campaignType === CampaignTypeMapping.Shopping;

export const isAudienceCampaign = (campaignType: string | number) =>
  campaignType === CampaignTypes.Audience ||
  campaignType === CampaignTypeMapping.Audience;

export const isLinkedInCampaign = (campaignType: string | number) =>
  campaignType === CampaignTypes.LinkedIn ||
  campaignType === CampaignTypeMapping.LinkedIn;

export const isAppCampaign = (campaignType: string | number) =>
  campaignType === CampaignTypes.App ||
  campaignType === CampaignTypeMapping.App;

interface Campaign {
  CampaignSubType: string | number;
  BiddingScheme: {
    Type: string;
  };
  IsDealCampaign: boolean;
  // Other properties
}

export const isAudienceCTVCampaign = (campaign: Campaign) =>
  isXandrVideoAdsCampaign(campaign.CampaignSubType) && campaign.BiddingScheme && campaign.BiddingScheme.Type === 'ManualCpv';

export const isAudienceDealCampaign = (campaign: Campaign) =>
  isXandrVideoAdsCampaign(campaign.CampaignSubType) && campaign.IsDealCampaign;

export const isAudienceShoppingCampaign = (campaignType: string | number, campaignSubType: string | number) => {
  const isAudienceType = campaignType === CampaignTypes.Audience || campaignType === CampaignTypeMapping.Audience;
  const isAudienceShopping = isAudienceType && (
    campaignSubType === CampaignSubTypes.AudienceFeedBased ||
    campaignSubType === CampaignSubTypes.AudienceDynamicFeedBased ||
    campaignSubType === CampaignSubTypeMapping.AudienceFeedBased ||
    campaignSubType === CampaignSubTypeMapping.AudienceDynamicFeedBased
  );

  return isAudienceShopping;
};

export const isSearchShoppingOrAudienceShoppingCampaign =
  (campaignType: string | number, campaignSubType: string | number) =>
    isAudienceShoppingCampaign(campaignType, campaignSubType) || isSearchShoppingCampaign(campaignType, campaignSubType);

export enum ProductGroupSubTypeEnum {
  standard = 1,
  coop = 2,
  all = 3,
}

export const campaignShouldServeOnMSAN = (campaignSettings: Array<{
  '@odata.type': string;
  ShouldServeOnMSAN: boolean | null | undefined;
}>) => {
  const deliverySetting = _.find(
    campaignSettings,
    setting => setting['@odata.type'].indexOf('DeliverySetting') !== -1
  );

  if (deliverySetting) {
    return deliverySetting.ShouldServeOnMSAN;
  }
  return false;
};

export const isXandrServing = (
  campaignSubType: string | number,
  campaignSettings: Array<{
    '@odata.type': string;
    ShouldServeOnMSAN: boolean | null | undefined;
  }>
) => isXandrCampaign(campaignSubType) && !campaignShouldServeOnMSAN(campaignSettings);

export const isMSANServingDNV = (
  campaignSubType: string | number,
  campaignSettings: Array<{
    '@odata.type': string;
    ShouldServeOnMSAN: boolean | null | undefined;
  }>
) => isXandrCampaign(campaignSubType) && campaignShouldServeOnMSAN(campaignSettings);

